<template>
  <div class="global-overview">
    <div class="page-header">
      <h1 class="page-title">{{ $t('settings.globalOverview') }}</h1>
      <el-tooltip effect="dark" :content="$t('settings.logDialog.logButtonTooltip')" placement="left">
        <el-button type="primary" icon="el-icon-tickets" circle @click="showLogDialog = true"></el-button>
      </el-tooltip>
    </div>

    <!-- Stats Cards Row -->
    <el-row :gutter="20" class="stats-cards">
      <el-col :xs="24" :sm="12" :md="12" :lg="6" :xl="6">
        <el-card shadow="hover" class="stats-card">
          <div class="stats-card-content">
            <div class="stats-icon-container">
              <i class="el-icon-office-building"></i>
            </div>
            <div class="stats-info">
              <h3>{{ $t('settings.tenants') }}</h3>
              <div class="stats-count">{{ tenantCount }}</div>
              <div class="stats-actions">
                <el-button
                  type="primary"
                  size="medium"
                  @click="$router.push({path: '/app/tenants'})">
                  {{ $t('settings.viewAll') }}
                </el-button>
                <el-button
                  type="success"
                  size="medium"
                  @click="$router.push({path: '/app/tenants/create'})">
                  {{ $t('settings.create') }}
                </el-button>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>

      <el-col :xs="24" :sm="12" :md="12" :lg="6" :xl="6">
        <el-card shadow="hover" class="stats-card">
          <div class="stats-card-content">
            <div class="stats-icon-container customer-icon">
              <i class="el-icon-user-solid"></i>
            </div>
            <div class="stats-info">
              <h3>{{ $t('settings.customers') }}</h3>
              <div class="stats-count">{{ customerCount }}</div>
              <div class="stats-actions">
                <el-button
                  type="primary"
                  size="medium"
                  @click="$router.push({path: '/app/global/customers'})">
                  {{ $t('settings.viewAll') }}
                </el-button>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>

      <el-col :xs="24" :sm="12" :md="12" :lg="6" :xl="6">
        <el-card shadow="hover" class="stats-card">
          <div class="stats-card-content">
            <div class="stats-icon-container account-icon">
              <i class="el-icon-user"></i>
            </div>
            <div class="stats-info">
              <h3>{{ $t('settings.accounts') }}</h3>
              <div class="stats-count">{{ accountCount }}</div>
              <div class="stats-actions">
                <el-button
                  type="primary"
                  size="medium"
                  @click="$router.push({path: '/app/global/accounts'})">
                  {{ $t('settings.viewAll') }}
                </el-button>
                <el-button
                  type="success"
                  size="medium"
                  @click="$router.push({path: '/app/global/accounts/create'})">
                  {{ $t('settings.create') }}
                </el-button>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>

      <el-col :xs="24" :sm="12" :md="12" :lg="6" :xl="6">
        <el-card shadow="hover" class="stats-card">
          <div class="stats-card-content">
            <div class="stats-icon-container server-icon">
              <i class="el-icon-connection"></i>
            </div>
            <div class="stats-info">
              <h3>{{ $t('settings.servers') }}</h3>
              <div class="stats-count">{{ serverCount }}</div>
              <div class="stats-actions">
                <el-button
                  type="primary"
                  size="medium"
                  @click="$router.push({path: '/app/global/servers'})">
                  {{ $t('settings.viewAll') }}
                </el-button>
              </div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- Recent Tenants Section -->
    <div class="section-container">
      <div class="table-section-header">
        <div class="table-section-header__left">
          <div class="table-section-header__icon">
            <i class="el-icon-s-cooperation"></i>
          </div>
          <div>
            <h2 class="table-section-header__title">{{ $t('settings.recentTenants') }}</h2>
            <p class="table-section-header__subtitle">{{ recentTenants.length }} {{ $t('settings.entries') }}</p>
          </div>
        </div>
      </div>

      <div class="table-card">
        <base-table
          :data="recentTenants"
          :loading="loadingTenants"
          :empty-text="$t('settings.tenantsTableEmpty')"
          style="width: 100%">

          <el-table-column prop="name" min-width="180" :label="$t('settings.name')">
            <template slot-scope="scope">
              <span class="cell-bold">{{ scope.row.name }}</span>
            </template>
          </el-table-column>

          <el-table-column prop="createdDate" min-width="190" :label="$t('settings.createdAt')">
            <template slot-scope="scope">
              <div class="cell-url" v-if="scope.row.createdDate">
                <i class="el-icon-time cell-url__icon"></i>
                <span>{{ scope.row.createdDate | moment($t('settings.dateTimeFormat')) }}</span>
              </div>
              <span class="cell-empty" v-else>—</span>
            </template>
          </el-table-column>

          <el-table-column prop="updatedDate" min-width="190" :label="$t('settings.updatedAt')">
            <template slot-scope="scope">
              <div class="cell-url" v-if="scope.row.updatedDate">
                <i class="el-icon-refresh cell-url__icon"></i>
                <span>{{ scope.row.updatedDate | moment($t('settings.dateTimeFormat')) }}</span>
              </div>
              <span class="cell-empty" v-else>—</span>
            </template>
          </el-table-column>

          <el-table-column fixed="right" align="center" width="100" :label="$t('settings.operations')">
            <template slot-scope="scope">
              <div class="action-cell">
                <el-tooltip :content="$t('settings.view')" placement="top">
                  <el-button
                    type="primary"
                    icon="el-icon-view"
                    size="mini"
                    circle
                    @click.stop.prevent="$router.push({path: `/app/tenants/${scope.row.id}/edit`})">
                  </el-button>
                </el-tooltip>
              </div>
            </template>
          </el-table-column>
        </base-table>
      </div>
    </div>

    <!-- Recent Customers Section -->
    <div class="section-container">
      <div class="table-section-header">
        <div class="table-section-header__left">
          <div class="table-section-header__icon">
            <i class="el-icon-office-building"></i>
          </div>
          <div>
            <h2 class="table-section-header__title">{{ $t('settings.recentCustomers') }}</h2>
            <p class="table-section-header__subtitle">{{ recentCustomers.length }} {{ $t('settings.entries') }}</p>
          </div>
        </div>
      </div>

      <div class="table-card">
        <base-table
          :data="recentCustomers"
          :loading="loadingCustomers"
          :empty-text="$t('settings.customersTableEmpty')"
          style="width: 100%">

          <el-table-column prop="name" min-width="180" :label="$t('settings.name')">
            <template slot-scope="scope">
              <span class="cell-bold">{{ scope.row.name }}</span>
            </template>
          </el-table-column>

          <el-table-column min-width="150" :label="$t('settings.tenantName')">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.tenant && scope.row.tenant.name" type="info" size="mini" class="domain-tag">
                {{ scope.row.tenant.name }}
              </el-tag>
              <span class="cell-empty" v-else>—</span>
            </template>
          </el-table-column>

          <el-table-column width="130" align="center" :label="$t('settings.userCount')">
            <template slot-scope="scope">
              <el-tag type="info" size="mini" class="domain-tag">
                <i class="el-icon-user"></i> {{ scope.row.userCount || 0 }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column width="150" align="center" :label="$t('settings.adConnectionCount')">
            <template slot-scope="scope">
              <el-tag type="info" size="mini" class="domain-tag">
                <i class="el-icon-connection"></i>
                {{ scope.row.adConnections ? scope.row.adConnections.length : 0 }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column prop="createdDate" min-width="190" :label="$t('settings.createdAt')">
            <template slot-scope="scope">
              <div class="cell-url" v-if="scope.row.createdDate">
                <i class="el-icon-time cell-url__icon"></i>
                <span>{{ scope.row.createdDate | moment($t('settings.dateTimeFormat')) }}</span>
              </div>
              <span class="cell-empty" v-else>—</span>
            </template>
          </el-table-column>

          <el-table-column prop="updatedDate" min-width="190" :label="$t('settings.updatedAt')">
            <template slot-scope="scope">
              <div class="cell-url" v-if="scope.row.updatedDate">
                <i class="el-icon-refresh cell-url__icon"></i>
                <span>{{ scope.row.updatedDate | moment($t('settings.dateTimeFormat')) }}</span>
              </div>
              <span class="cell-empty" v-else>—</span>
            </template>
          </el-table-column>

          <el-table-column fixed="right" align="center" width="100" :label="$t('settings.operations')">
            <template slot-scope="scope">
              <div class="action-cell">
                <el-tooltip :content="$t('settings.view')" placement="top">
                  <el-button
                    type="primary"
                    icon="el-icon-view"
                    size="mini"
                    circle
                    :disabled="!scope.row.tenant"
                    @click.stop.prevent="viewCustomer(scope.row)">
                  </el-button>
                </el-tooltip>
              </div>
            </template>
          </el-table-column>
        </base-table>
      </div>
    </div>

    <!-- Log Dialog -->
    <LogDialog :visible="showLogDialog" @close="onLogDialogClose()"></LogDialog>
  </div>
</template>

<script>
import {tenantService} from '@/services/TenantService.class';
import {adminAccountService} from '@/services/AdminAccountService.class';
import {adminCustomerService} from '@/services/AdminCustomerService.class';
import {ldapServerService} from '@/services/LdapServerService.class';
import {entraConnectionService} from '@/services/EntraConnectionService.class';
import LogDialog from "@/components/settings/LogDialog";
import BaseTable from '@/components/common/BaseTable';

export default {
  name: "GlobalOverview",
  components: {
    LogDialog,
    BaseTable
  },
  data() {
    return {
      tenantCount: 0,
      customerCount: 0,
      accountCount: 0,
      ldapServerCount: 0,
      entraConnectionCount: 0,
      recentTenants: [],
      recentCustomers: [],
      loadingTenants: false,
      loadingCustomers: false,
      loadingAccounts: false,
      loadingServers: false,
      showLogDialog: false
    };
  },
  computed: {
    serverCount() {
      return this.ldapServerCount + this.entraConnectionCount;
    }
  },
  mounted() {
    this.loadStats();
  },
  methods: {
    loadStats() {
      this.loadTenants();
      this.loadCustomers();
      this.loadAccountCount();
      this.loadServerCounts();
    },
    loadServerCounts() {
      this.loadingServers = true;

      Promise.all([
        ldapServerService.getAll(),
        entraConnectionService.getAll()
      ])
        .then(([ldapResponse, entraResponse]) => {
          this.ldapServerCount = Array.isArray(ldapResponse.data) ? ldapResponse.data.length : 0;
          this.entraConnectionCount = Array.isArray(entraResponse.data) ? entraResponse.data.length : 0;
        })
        .catch(() => {
          this.ldapServerCount = 0;
          this.entraConnectionCount = 0;
        })
        .finally(() => {
          this.loadingServers = false;
        });
  },
    loadTenants() {
      this.loadingTenants = true;

      tenantService.get()
        .then(response => {
          const tenants = response.data;
          this.tenantCount = tenants.length;
          // Sort by creation date (newest first) if available
          if (tenants.length > 0 && tenants[0].createdAt) {
            this.recentTenants = [...tenants]
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
              .slice(0, 5); // Show 5 most recent tenants
          } else {
            this.recentTenants = tenants.slice(0, 5);
          }
        })
        .catch(error => {
          console.error('Error loading tenants:', error);
          this.$message.error(this.$t('settings.genericHttpError'));
        })
        .finally(() => {
          this.loadingTenants = false;
        });
    },
    loadCustomers() {
      this.loadingCustomers = true;

      // Use the admin customer service to get all customers
      adminCustomerService.getAllCustomers()
        .then(response => {
          let customers = [];

          if (response.data.content) {
            // Paginated response
            customers = response.data.content;
            this.customerCount = response.data.totalElements;
          } else if (Array.isArray(response.data)) {
            // Array response
            customers = response.data;
            this.customerCount = customers.length;
          }

          // Sort by creation date (newest first) if available
          if (customers.length > 0 && customers[0].createdAt) {
            this.recentCustomers = [...customers]
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
              .slice(0, 5); // Show 5 most recent customers
          } else {
            this.recentCustomers = customers.slice(0, 5);
          }
        })
        .catch(error => {
          console.error('Error loading customers:', error);
          this.$message.error(this.$t('settings.genericHttpError'));
        })
        .finally(() => {
          this.loadingCustomers = false;
        });
    },
    loadAccountCount() {
      this.loadingAccounts = true;

      adminAccountService.getAllAccountsCount()
        .then(response => {
          if (response.data.count) {
            this.accountCount = response.data.count;
          } else {
            // No count available
            this.accountCount = "?";
          }
        })
        .catch(error => {
          console.error('Error loading account count:', error);
          this.accountCount = "?";
        })
        .finally(() => {
          this.loadingAccounts = false;
        });
    },
    viewCustomer(customer) {
      if (customer.tenant) {
        this.$router.push({
          path: `/app/tenants/${customer.tenant.id}/customers/${customer.id}/edit`
        });
      }
    },
    onLogDialogClose() {
      this.showLogDialog = false;
    }
  }
}
</script>

<style scoped lang="scss">
.global-overview {
  padding: 0 15px;

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;

    .page-title {
      margin: 0;
      color: #303133;
      font-weight: 500;
    }
  }

  .stats-cards {
    margin-bottom: 40px;

    .stats-card {
      height: 100%;
      transition: all 0.3s;

      &:hover {
        transform: translateY(-5px);
      }

      .stats-card-content {
        display: flex;
        align-items: center;

        .stats-icon-container {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 64px;
          height: 64px;
          border-radius: 12px;
          background-color: #409EFF;
          margin-right: 20px;

          i {
            font-size: 32px;
            color: white;
          }

          &.customer-icon {
            background-color: #67C23A;
          }

          &.account-icon {
            background-color: #E6A23C;
          }

          &.server-icon {
            background-color: var(--entra, #6366f1);
          }
        }

        .stats-info {
          flex: 1;

          h3 {
            margin: 0 0 5px 0;
            color: #606266;
            font-size: 16px;
          }

          .stats-count {
            font-size: 28px;
            font-weight: bold;
            color: #303133;
            margin-bottom: 15px;
          }

          .stats-actions {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;

            .el-button {
              margin: 0;
            }
          }
        }
      }
    }
  }

  .section-container {
    margin-bottom: 30px;

    .table-section-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 20px;

      &__left {
        display: flex;
        align-items: center;
        gap: 14px;
      }

      &__icon {
        width: 42px;
        height: 42px;
        border-radius: 10px;
        background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        i {
          color: var(--bg-surface);
          font-size: 18px;
        }
      }

      &__title {
        margin: 0 0 2px;
        font-size: 17px;
        font-weight: 600;
        color: var(--text-primary);
        line-height: 1.3;
      }

      &__subtitle {
        margin: 0;
        font-size: 12px;
        color: var(--text-secondary);
        line-height: 1.4;
      }
    }

    .table-card {
      background: var(--bg-surface);
      border-radius: 12px;
      border: 1px solid var(--border);
      overflow: hidden;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
    }
  }

  .cell-bold {
    font-weight: 600;
    font-size: 13.5px;
    color: var(--text-primary);
  }

  .cell-url {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--text-secondary);
    font-size: 13px;

    &__icon {
      color: var(--text-muted);
      font-size: 14px;
      flex-shrink: 0;
    }
  }

  .cell-empty {
    color: var(--text-muted);
  }

  .domain-tag {
    font-weight: 500;
    border-radius: 6px;
  }

  .action-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }

  // Responsive adjustments
  @media (max-width: 767px) {
    .stats-cards {
      .stats-card {
        margin-bottom: 20px;

        .stats-card-content {
          flex-direction: column;
          text-align: center;

          .stats-icon-container {
            margin-right: 0;
            margin-bottom: 15px;
          }

          .stats-info {
            .stats-actions {
              justify-content: center;
            }
          }
        }
      }
    }
  }
}
</style>
